import React, { useState, useMemo } from 'react';
import useCachedFetch from '../hooks/useCachedFetch';
import TeamCard from '../components/TeamCard';
import SEO from '../components/SEO';
import { FaSearch, FaTimes, FaUsers } from 'react-icons/fa';
import '../styles/Team.css';

// Exact order from category.txt
const ORDERED_CATEGORIES = [
    "Server Development",
    "Server Operations",
    "Map Development",
    "Vehicle Development",
    "Brand Management",
    "Discord Management",
    "Vehicle Livery Development",
    "Economic Advisory",
    "Website Management",
    "Professional Advisory Team",
    "EUP / Dress Management",
    "QA & Testing Team",
    "RP Facilitator",
    "Chat Support"
];

const Team = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const { data, loading, error } = useCachedFetch(`${backendUrl}/api/team`, { cacheDuration: 1 * 60 * 1000 });
    const [activeSection, setActiveSection] = useState('ALL');
    const [searchQuery, setSearchQuery] = useState('');

    // Primary category grouping for ALL view (prevents duplicate cards when scrolling all sections)
    const groupedMembers = useMemo(() => {
        const teamMembers = data || [];
        const groups = {};

        ORDERED_CATEGORIES.forEach(cat => {
            groups[cat] = [];
        });

        teamMembers.forEach(member => {
            if (!member.categories) {
                groups[ORDERED_CATEGORIES[0]].push(member);
                return;
            }
            const cats = member.categories.split(',').map(c => c.trim()).filter(Boolean);
            if (cats.length > 0) {
                // Find first matching category in ORDERED_CATEGORIES
                const primaryCat = cats.find(c => ORDERED_CATEGORIES.includes(c)) || ORDERED_CATEGORIES[0];
                if (groups[primaryCat]) {
                    groups[primaryCat].push(member);
                } else {
                    groups[ORDERED_CATEGORIES[0]].push(member);
                }
            } else {
                groups[ORDERED_CATEGORIES[0]].push(member);
            }
        });

        return groups;
    }, [data]);

    // Calculate count badges: total unique for ALL, and total members associated with each category
    const categoryCounts = useMemo(() => {
        const counts = {};
        const teamMembers = data || [];
        
        counts['ALL'] = teamMembers.length;

        ORDERED_CATEGORIES.forEach(cat => {
            // Count all members associated with this category
            const count = teamMembers.filter(m => {
                if (!m.categories) return false;
                const cats = m.categories.split(',').map(c => c.trim());
                return cats.includes(cat);
            }).length;
            counts[cat] = count;
        });

        return counts;
    }, [data]);

    // Dynamic member lookup for each section
    const getMembersForCategory = (category) => {
        const query = searchQuery.toLowerCase().trim();

        if (activeSection === 'ALL') {
            const members = groupedMembers[category] || [];
            if (!query) return members;
            return members.filter(m => {
                const nameMatch = m.displayName?.toLowerCase().includes(query);
                const userMatch = m.username?.toLowerCase().includes(query);
                const akaMatch = m.description?.toLowerCase().includes(query);
                const catMatch = m.categories?.toLowerCase().includes(query);
                return nameMatch || userMatch || akaMatch || catMatch;
            });
        } else {
            // Specific tab view: return all members belonging to this category
            const teamMembers = data || [];
            return teamMembers.filter(m => {
                if (!m.categories) return false;
                const cats = m.categories.split(',').map(c => c.trim());
                const inCat = cats.includes(category);
                if (!inCat) return false;
                if (!query) return true;
                const nameMatch = m.displayName?.toLowerCase().includes(query);
                const userMatch = m.username?.toLowerCase().includes(query);
                const akaMatch = m.description?.toLowerCase().includes(query);
                const catMatch = m.categories?.toLowerCase().includes(query);
                return nameMatch || userMatch || akaMatch || catMatch;
            });
        }
    };

    const handleCategoryClick = (category) => {
        setActiveSection(category);

        // Ensure React finishes rendering DOM before scrolling
        setTimeout(() => {
            if (category === 'ALL') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            const el = document.getElementById(`cat-${category.replace(/\s+/g, '-').toLowerCase()}`);
            if (el) {
                const navSection = document.querySelector('.team-nav-section');
                const stickyNavBottom = navSection ? navSection.getBoundingClientRect().bottom : 235;
                const elTop = el.getBoundingClientRect().top;
                
                // Calculate exact scroll needed to place heading right under sticky category bar
                const targetScrollY = window.pageYOffset + (elTop - stickyNavBottom) - 15;

                window.scrollTo({
                    top: Math.max(0, targetScrollY),
                    behavior: 'smooth'
                });
            }
        }, 50);
    };

    const hasAnyResults = useMemo(() => {
        if (!searchQuery.trim()) return true;
        return ORDERED_CATEGORIES.some(cat => getMembersForCategory(cat).length > 0);
    }, [searchQuery, activeSection, data, groupedMembers]);

    return (
        <div className="team-page-container">
            <SEO
                title="Staff Team & Administration"
                description="Meet the developers, server administrators, moderators, and support team behind Thalai Nagaram RP (TNRP)."
                keywords="TNRP Staff Team, Thalai Nagaram RP Admins, Tamil FiveM Developers, TNRP Administration, FiveM Staff Tamil"
                canonicalUrl="https://thalainagaram.org/team"
            />
            {/* Hero / Header */}
            <div className="team-hero">
                <h1>OUR <span className="highlight">TEAM</span></h1>
                <p className="team-subtitle">Meet the dedicated team powering Thalainagaram RP</p>
                
                {/* Search Bar */}
                <div className="team-search-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        className="team-search-input"
                        placeholder="Search team members by name, handle, or category..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="search-clear-btn" onClick={() => setSearchQuery('')} aria-label="Clear search">
                            <FaTimes />
                        </button>
                    )}
                </div>
            </div>

            {/* Fixed Sticky Category Navigation Bar */}
            <div className="team-nav-section">
                <div className="team-nav-header">
                    <span className="nav-label">
                        <FaUsers className="nav-icon" /> Select Category ({ORDERED_CATEGORIES.length})
                    </span>
                </div>

                <div className="categories-wrapper">
                    {/* ALL CATEGORIES PILL */}
                    <button
                        className={`category-pill ${activeSection === 'ALL' ? 'active' : ''}`}
                        onClick={() => handleCategoryClick('ALL')}
                    >
                        <span>All Categories</span>
                        <span className="count-badge">{categoryCounts['ALL']}</span>
                    </button>

                    {/* INDIVIDUAL CATEGORIES */}
                    {ORDERED_CATEGORIES.map(category => (
                        <button
                            key={category}
                            className={`category-pill ${activeSection === category ? 'active' : ''}`}
                            onClick={() => handleCategoryClick(category)}
                        >
                            <span>{category}</span>
                            <span className="count-badge">{categoryCounts[category] || 0}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="team-content-area">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading Team Members...</p>
                    </div>
                ) : error ? (
                    <div className="error-state">
                        <p>Error loading team members.</p>
                        <p className="error-detail">{error}</p>
                    </div>
                ) : !hasAnyResults ? (
                    <div className="no-results-state">
                        <h3>No members found matching "{searchQuery}"</h3>
                        <button className="reset-search-btn" onClick={() => setSearchQuery('')}>
                            Clear Search Filter
                        </button>
                    </div>
                ) : (
                    <div className="team-sections-list">
                        {ORDERED_CATEGORIES.map(category => {
                            // Filter by active category if not ALL
                            if (activeSection !== 'ALL' && activeSection !== category) return null;

                            const members = getMembersForCategory(category);
                            
                            // If searching and this category has no matches, hide it
                            if (searchQuery.trim() && members.length === 0) return null;

                            const sectionId = `cat-${category.replace(/\s+/g, '-').toLowerCase()}`;

                            return (
                                <section key={category} id={sectionId} className="category-group">
                                    <div className="category-header">
                                        <h2>{category}</h2>
                                        <span className="category-member-count">
                                            {members.length} {members.length === 1 ? 'Member' : 'Members'}
                                        </span>
                                        <div className="header-line"></div>
                                    </div>

                                    {members.length === 0 ? (
                                        <div className="empty-category">No members currently assigned to this category.</div>
                                    ) : (
                                        <div className="team-grid">
                                            {members.map(member => (
                                                <TeamCard key={`${category}-${member.id}`} member={member} />
                                            ))}
                                        </div>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Team;
